import { ConnectError } from "@connectrpc/connect";
import { captureException } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import type { FundSource } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import Cancel from "~icons/material-symbols/cancel-outline";
import CheckCircle from "~icons/material-symbols/check-circle-outline";
import QrCodeScanner from "~icons/material-symbols/qr-code-scanner";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import { LoadingIcon } from "../../../components/loadingIcon";
import { AppError, ERROR_CODES } from "../../../libs/errors";
import { t } from "../../../libs/i18n";
import { scanQrCode } from "../../../libs/qr-scanner";
import { formatNumber } from "../../../libs/utils";

export const Route = createFileRoute("/_authed/receive/")({
  component: RouteComponent,
});

type CodeDetail = {
  amount: bigint;
  fundSource: FundSource;
};

type ModalState =
  | { type: "confirmation"; detail: CodeDetail; code: string }
  | { type: "loading"; detail: CodeDetail; code: string }
  | { type: "success" }
  | { type: "error"; message: string };

function RouteComponent() {
  const navigate = useNavigate();
  const { client, tenant } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!code.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await client.getStandaloneDistributionCodeDetail({
        distributionCode: code.trim(),
      });

      if (!response.fundSource) {
        throw new Error("No fund source found");
      }

      setModalState({
        type: "confirmation",
        detail: {
          amount: response.amount,
          fundSource: response.fundSource,
        },
        code: code.trim(),
      });
    } catch (error) {
      let errorMessage = t("配布コードが利用できません");
      if (error instanceof ConnectError) {
        if (error.message.includes("failed_precondition")) {
          errorMessage = t("このコードは既に使用されています");
        }
      }

      const appError = new AppError(
        ERROR_CODES.CONNECT_GET_STANDALONE_DISTRIBUTION_CODE_DETAIL_FAILED,
        error instanceof Error ? error.message : "Unknown error",
        { originalError: error },
      );
      captureException(appError);
      setModalState({ type: "error", message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  }, [code, client]);

  const handleAccept = useCallback(async () => {
    if (modalState?.type !== "confirmation") return;

    setModalState({
      type: "loading",
      detail: modalState.detail,
      code: modalState.code,
    });

    try {
      await client.consumeStandaloneDistributionCode({
        distributionCode: modalState.code,
      });
      // ポイント受け取り完了時に取引履歴のキャッシュを破棄
      queryClient.resetQueries({ queryKey: ["transactions"] });
      setModalState({ type: "success" });
    } catch (error) {
      let errorMessage = t("配布コードが利用できません");
      if (error instanceof ConnectError) {
        if (error.message.includes("failed_precondition")) {
          errorMessage = t("このコードは既に使用されています");
        }
      }

      const appError = new AppError(
        ERROR_CODES.CONNECT_CONSUME_STANDALONE_DISTRIBUTION_CODE_FAILED,
        error instanceof Error ? error.message : "Unknown error",
        { originalError: error },
      );
      captureException(appError);
      setModalState({ type: "error", message: errorMessage });
    }
  }, [modalState, client, queryClient]);

  const handleCancel = useCallback(() => {
    setModalState(null);
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setModalState(null);
    setCode("");
    navigate({ to: "/" });
  }, [navigate]);

  const handleCloseError = useCallback(() => {
    setModalState(null);
  }, []);

  const handleScanQr = useCallback(async () => {
    await scanQrCode({
      filterServiceIds: [tenant.serviceId],
      navigateFn: navigate,
    });
  }, [navigate, tenant.serviceId]);

  return (
    <>
      <Header
        title={t("ポイントを受け取る")}
        onClickBack={() => navigate({ to: "/" })}
      />
      <div
        className={css({
          pt: "calc(48px + env(safe-area-inset-top, 0px))",
          px: "20px",
          pb: "calc(88px + env(safe-area-inset-bottom, 0px))",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        })}
      >
        {/* コード入力セクション */}
        <div
          className={css({
            bg: "surface.primary",
            rounded: "16px",
            p: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          })}
        >
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            })}
          >
            <h2
              className={css({
                fontSize: "16px",
                fontWeight: "bold",
                color: "text.primary",
              })}
            >
              {t("コード入力で受け取る")}
            </h2>
            <p
              className={css({
                fontSize: "14px",
                color: "text.secondary",
                lineHeight: "1.5",
              })}
            >
              {t(
                "ポイント受け取りコードを入力して「送信」をタップしてください。",
              )}
            </p>
          </div>
          <div
            className={css({
              display: "flex",
              alignItems: "center",
              gap: "8px",
            })}
          >
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t("受け取りコードを入力")}
              className={css({
                flex: 1,
                border: "1px solid",
                borderColor: "border.primary",
                rounded: "8px",
                px: "12px",
                py: "12px",
                fontSize: "16px",
                outline: "none",
                "&:focus": {
                  borderColor: "border.accentPrimary",
                },
              })}
            />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !code.trim()}
              className={css({
                bg: code.trim() ? "surface.secondary" : "surface.secondary",
                color: code.trim() ? "text.primary" : "text.tertiary",
                border: "1px solid",
                borderColor: "border.secondary",
                rounded: "8px",
                px: "16px",
                py: "12px",
                fontSize: "14px",
                fontWeight: "bold",
                cursor: code.trim() ? "pointer" : "not-allowed",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "64px",
              })}
            >
              {isSubmitting ? <LoadingIcon /> : t("送信")}
            </button>
          </div>
        </div>

        {/* QR読み取りセクション */}
        <div
          className={css({
            bg: "surface.primary",
            rounded: "16px",
            p: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          })}
        >
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            })}
          >
            <h2
              className={css({
                fontSize: "16px",
                fontWeight: "bold",
                color: "text.primary",
              })}
            >
              {t("QRコード読み取りで受け取る")}
            </h2>
            <p
              className={css({
                fontSize: "14px",
                color: "text.secondary",
                lineHeight: "1.5",
              })}
            >
              {t("ポイント受け取り用のQRコードを読み取ってください。")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleScanQr}
            className={css({
              bg: "surface.secondary",
              color: "text.primary",
              border: "1px solid",
              borderColor: "border.secondary",
              rounded: "8px",
              px: "16px",
              py: "12px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            })}
          >
            <QrCodeScanner className={css({ fontSize: "20px" })} />
            {t("読み取り")}
          </button>
        </div>
      </div>

      {/* モーダル */}
      {modalState && (
        <ReceiveCodeModal
          state={modalState}
          onAccept={handleAccept}
          onCancel={handleCancel}
          onCloseSuccess={handleCloseSuccess}
          onCloseError={handleCloseError}
        />
      )}
    </>
  );
}

type ReceiveCodeModalProps = {
  state: ModalState;
  onAccept: () => void;
  onCancel: () => void;
  onCloseSuccess: () => void;
  onCloseError: () => void;
};

function ReceiveCodeModal({
  state,
  onAccept,
  onCancel,
  onCloseSuccess,
  onCloseError,
}: ReceiveCodeModalProps) {
  const ErrorComponent = useMemo(() => {
    if (state.type !== "error") return null;
    return (
      <div
        className={css({
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "80%",
          maxWidth: "360px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        })}
      >
        <h2
          className={css({
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "24px",
          })}
        >
          {state.message}
        </h2>

        <div
          className={css({
            borderRadius: "8px",
            marginBottom: "24px",
          })}
        >
          <div
            className={css({
              fontSize: "56px",
              fontWeight: "bold",
              color: "text.accentError",
            })}
          >
            <Cancel />
          </div>
        </div>

        <button
          type="button"
          className={css({
            padding: "12px",
            borderRadius: "8px",
            width: "100%",
            fontWeight: "bold",
            cursor: "pointer",
            backgroundColor: "surface.secondary",
            color: "text.primary",
            border: "1px solid",
            borderColor: "border.secondary",
          })}
          onClick={onCloseError}
        >
          {t("閉じる")}
        </button>
      </div>
    );
  }, [state, onCloseError]);

  const SuccessComponent = useMemo(() => {
    if (state.type !== "success") return null;
    return (
      <div
        className={css({
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "80%",
          maxWidth: "360px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        })}
      >
        <h2
          className={css({
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "24px",
          })}
        >
          {t("ポイント受け取り完了")}
        </h2>

        <div
          className={css({
            borderRadius: "8px",
            marginBottom: "24px",
          })}
        >
          <div
            className={css({
              fontSize: "56px",
              fontWeight: "bold",
              color: "text.accentSuccess",
            })}
          >
            <CheckCircle />
          </div>
        </div>

        <button
          type="button"
          className={css({
            padding: "12px",
            borderRadius: "8px",
            width: "100%",
            fontWeight: "bold",
            cursor: "pointer",
            backgroundColor: "surface.secondary",
            color: "text.primary",
            border: "1px solid",
            borderColor: "border.secondary",
          })}
          onClick={onCloseSuccess}
        >
          {t("閉じる")}
        </button>
      </div>
    );
  }, [state.type, onCloseSuccess]);

  const ConfirmationComponent = useMemo(() => {
    if (state.type !== "confirmation" && state.type !== "loading") return null;
    const isLoading = state.type === "loading";
    const detail = state.detail;

    return (
      <div
        className={css({
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "80%",
          maxWidth: "360px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        })}
      >
        <h2
          className={css({
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "24px",
          })}
        >
          {t("ポイントを受け取りますか？")}
        </h2>

        <div
          className={css({
            width: "100%",
            marginBottom: "16px",
          })}
        >
          <div
            className={css({
              backgroundColor: "surface.accentSuccessLight",
              padding: "16px",
              borderRadius: "8px",
              textAlign: "center",
            })}
          >
            <div
              className={css({
                fontSize: "14px",
                fontWeight: "bold",
                color: "text.secondary",
                marginBottom: "4px",
              })}
            >
              {detail.fundSource.name}
            </div>
            <div
              className={css({
                display: "flex",
                alignItems: "baseline",
                justifyContent: "center",
                gap: "4px",
              })}
            >
              <span
                className={css({
                  fontSize: "32px",
                  fontWeight: "bold",
                  color: "text.accentSuccess",
                })}
              >
                {formatNumber(Number(detail.amount))}
              </span>
              <span
                className={css({
                  fontSize: "18px",
                  fontWeight: "normal",
                  color: "text.secondary",
                })}
              >
                pt
              </span>
            </div>
          </div>
        </div>

        <button
          type="button"
          className={css({
            backgroundColor: isLoading
              ? "surface.secondary"
              : "text.accentPrimary",
            color: "white",
            padding: "12px",
            borderRadius: "8px",
            width: "100%",
            fontWeight: "bold",
            cursor: isLoading ? "not-allowed" : "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "8px",
          })}
          onClick={onAccept}
          disabled={isLoading}
        >
          {isLoading ? <LoadingIcon /> : t("受け取る")}
        </button>

        <button
          type="button"
          className={css({
            padding: "12px",
            borderRadius: "8px",
            width: "100%",
            fontWeight: "bold",
            cursor: isLoading ? "not-allowed" : "pointer",
            opacity: isLoading ? 0.5 : 1,
            color: "text.primary",
          })}
          disabled={isLoading}
          onClick={onCancel}
        >
          {t("キャンセル")}
        </button>
      </div>
    );
  }, [state, onAccept, onCancel]);

  return (
    <div
      className={css({
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(226, 226, 226, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      })}
    >
      {state.type === "error" && ErrorComponent}
      {state.type === "success" && SuccessComponent}
      {(state.type === "confirmation" || state.type === "loading") &&
        ConfirmationComponent}
    </div>
  );
}
